import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useToast } from '../../../../../../modules/components/toast/ToastContext';
import { FieldStatus, SiService, siteIntelligenceServiceLabel } from '../../../../mappings';
import KeywordTypeBadge from '../../../../../../modules/components/badge/KeywordTypeBadge';
import clsx from 'clsx';
import UrlLabelThumbnailCard from '../../card/UrlLabelThumbnailCard';
import UnlabeledBulletColumns from '../../../../../../modules/components/list/UnlabeledBulletColumns';
import { getActiveItemsOnly, getImagePathByCaptureId, getTranslatedValueWithFallback } from '../../utils';
import ToggleLabelCard from '../../card/ToggleLabelCard';
import ToggleSwitch from '../../../../../../modules/components/toggleSwitch/ToggleSwitch';
import { getCardId } from './CategoryDetail';
import { UsePageDataManagerResult } from '../../hooks/usePageDataManager';
import { SiAiLinksModal } from '../../modal/SiAiLinksModal';
import SingleAccordion from '../../../../../../modules/components/accordion/SingleAccordion';

interface Props {
  data: any;
  screenshots?: any[];
  status: string;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
  onExpandImages: (images: string[], title?: string) => void;
  isEditable?: boolean;
  isSiAiNewData?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
}

export default function TobaccoContent({
  data,
  screenshots,
  status,
  translation,
  currentLanguage,
  refetch,
  onExpandImages,
  isEditable,
  isSiAiNewData,
  onUpdate,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_CATEGORY_DETAIL.${id}` }, values);
  const { setToast } = useToast();
  const emDash = '—';
  const shouldRenderSiAiReadonlyStyle = !isEditable && !!isSiAiNewData;
  const [showTobaccoLinksModal, setShowTobaccoLinksModal] = useState(false);
  const [editingUrlKey, setEditingUrlKey] = useState<number | null>(null);
  const [editingUrlValue, setEditingUrlValue] = useState('');

  const isTobacco = data?.result?.tobacco ?? false;
  const isCuban = data?.result?.cuban ?? false;
  const tobaccoDetailLinks: string[] = (Array.isArray(data?.detail) ? data.detail : [])
    .map((item: any) => item?.url)
    .filter((url: unknown): url is string => typeof url === 'string');
  const tobaccoLinks: string[] = [...tobaccoDetailLinks];
  const hasTobaccoLinks = tobaccoLinks.length > 0;
  const activeCubanBrands = (data?.result?.cuban_cigar_brands ?? []).filter(
    (brandObj) => brandObj?.field_status === FieldStatus.ACTIVE
  );

  if (shouldRenderSiAiReadonlyStyle) {
    return (
      <>
        <div
          className={clsx('card border', !isTobacco && 'opacity-50')}
          id={getCardId(SiService.TOBACCO_SIFT_SERVICE)}
          style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
        >
          <div
            className='card-body p-6 vstack gap-4'
            style={{ cursor: 'pointer' }}
            onClick={() => setShowTobaccoLinksModal(true)}
          >
            <div className='d-flex align-items-start justify-content-between gap-3'>
              <div className='d-flex align-items-start gap-3'>
                <i className='bi bi-tag-fill text-warning fs-4 mt-1'></i>
                <div className='d-flex flex-column gap-2'>
                  <div className='fw-bolder'>
                    {intl.formatMessage({ id: `SI_KEYWORD_TYPE_BADGE.${SiService.TOBACCO_SIFT_SERVICE}` })}
                  </div>
                  <div>{getTranslatedValueWithFallback(
                    translation,
                    currentLanguage,
                    `sensitive_category.tobacco_reason`,
                    data?.result?.tobacco_reason ?? emDash
                  )}</div>
                </div>
              </div>
              {hasTobaccoLinks && (
                <i
                  className='bi bi-link-45deg fs-5 text-muted'
                  aria-hidden='true'
                ></i>
              )}
            </div>

            {(isCuban || activeCubanBrands.length > 0) && (
              <div
                className='border rounded p-4'
                style={{ backgroundColor: '#fff5f7', borderColor: '#fecaca' }}
              >
                <div className='d-flex align-items-start justify-content-between gap-3'>
                  <div className='d-flex align-items-start gap-3'>
                    <i className='bi bi-exclamation-circle-fill text-danger mt-1'></i>
                    <div className='d-flex flex-column gap-2'>
                      <div className='fw-bolder text-danger'>{t('CUBAN_CIGAR')}</div>
                      <div className='fst-italic'>
                        {data?.result?.cuban_related_law ?? emDash}
                      </div>

                      {activeCubanBrands.length > 0 && (
                        <div className='d-flex flex-wrap gap-2 mt-1'>
                          {t('BRANDS_LABEL')}{' '}
                          {activeCubanBrands.map((brandObj, index) => (
                            <div
                              key={`${brandObj?.brand ?? 'brand'}-${index}`}
                              className='badge  fs-8'
                              style={{ backgroundColor: '#ffdbdb', color: '#7f1d1d' }}
                            >
                              {brandObj?.brand}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className='badge fw-bolder'
                    style={{ backgroundColor: '#fecaca', color: '#7f1d1d' }}
                  >
                    {t('TRIGGERED')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <SiAiLinksModal
          show={showTobaccoLinksModal}
          onClose={() => setShowTobaccoLinksModal(false)}
          title={t('REFERENCE_URLS')}
          links={tobaccoLinks}
        />
      </>
    );
  }

  return (
    <>
      <div
        className={clsx('card border', !isTobacco && 'opacity-50')}
        id={getCardId(SiService.TOBACCO_SIFT_SERVICE)}
        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
      >
        {isEditable && (
          <div className='card-header px-6'>
            <h4 className='card-title gap-4'>
              <div className='hstack gap-2'>
                <KeywordTypeBadge
                  type={SiService.TOBACCO_SIFT_SERVICE}
                  className={clsx('fs-base', !isTobacco && 'opacity-50')}
                />
              </div>
              <ToggleSwitch
                isVisible={isTobacco}
                onToggle={() => {
                  onUpdate(SiService.TOBACCO_SIFT_SERVICE, (data) => {
                    const newFieldStatus = isTobacco ? FieldStatus.INACTIVE : FieldStatus.ACTIVE;
                    data.result.tobacco = !isTobacco;
                    data.result.cuban = isTobacco ? false : data.result.cuban;
                    data.result.cuban_cigar_brands.forEach(
                      (c) => (c.field_status = newFieldStatus)
                    );
                    data.detail?.forEach((d) => {
                      d.cuban_cigar_brands?.forEach((c) => {
                        c.field_status = newFieldStatus;
                      });
                    });
                  });
                }}
              />
            </h4>
            <div className='card-toolbar gap-4'>
              <div className='form-check form-switch form-check-custom form-check-solid'>
                <label className='form-check-label m-0 me-2' htmlFor='cuban-switch'>
                  {t('CUBAN')}
                </label>
                <input
                  className='form-check-input w-30px h-20px'
                  type='checkbox'
                  value=''
                  id='cuban-switch'
                  disabled={!isTobacco}
                  checked={data?.result?.cuban}
                  onChange={(e) => {
                    onUpdate(SiService.TOBACCO_SIFT_SERVICE, (data) => {
                      data.result.cuban = e.target.checked;
                    });
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <div className='card-body p-0'>
          {!isSiAiNewData && isEditable && (data?.result?.tobacco_detected_by || data?.result?.tobacco_reason) && (
            <div className='bg-light py-4 px-6 d-flex flex-column gap-4'>
              <div className='d-flex flex-column flex-md-row gap-4 gap-md-9'>
                <div className='d-flex flex-column flex-shrink-0'>
                  <span className='fw-bolder fs-7'>Detect By</span>
                  <span className='fs-7'>{data?.result?.tobacco_detected_by}</span>
                </div>
                {data?.result?.tobacco_reason && (
                  <div className='d-flex flex-column'>
                    <span className='fw-bolder fs-7'>AI Reason</span>
                    <span className='fs-7'>{data?.result?.tobacco_reason}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {isSiAiNewData && (
            <div className='bg-light py-4 px-6 d-flex flex-column gap-4'>
              <div className='d-flex flex-column flex-md-row gap-4 gap-md-9'>
                <div className='d-flex flex-column flex-shrink-0'>
                  <span className='fw-bolder fs-7'>Detect By</span>
                  <span className='fs-7'>AI</span>
                </div>
                <div className='d-flex flex-column'>
                  <span className='fw-bolder fs-7'>AI Reason</span>
                  <span className='fs-7'>{data?.result?.tobacco_reason}</span>
                </div>
              </div>
            </div>
          )}

          <div className='p-6 vstack gap-4'>
            {data?.detail?.map((item, index) => {
              const hasActiveCubanBrands =
                item?.cuban_cigar_brands?.length > 0 &&
                item?.cuban_cigar_brands?.some(
                  (cuban) => cuban.field_status === FieldStatus.ACTIVE
                );
              const images = item?.capture_id
                ? [getImagePathByCaptureId(item?.capture_id, screenshots)]
                : [];
              const editableHeader = (
                <UrlLabelThumbnailCard
                  url={item.url}
                  thumbnail={images?.[0]}
                  onClickThumbnail={() => {
                    onExpandImages(images);
                  }}
                  showThumbnaiPlaceholder
                  className={{ container: 'p-3' }}
                />
              );
              const bodyContent =
                (isEditable || hasActiveCubanBrands) && (
                  <div className='d-flex flex-column gap-6 p-6 pt-4 border-top'>
                    {item?.cuban_cigar_brands?.length > 0 && (
                      <div className='d-flex flex-column gap-2'>
                        <div className='fw-bold'>{t('CUBAN_CIGAR_BRANDS_FOUND')}</div>
                        {isEditable ? (
                          <div className='row row-cols-1 row-cols-md-2 row-cols-xl-4 row-cols-xxl-5 g-2'>
                            {item?.cuban_cigar_brands?.map((cuban, cIndex) => (
                              <div key={cIndex}>
                                <ToggleLabelCard
                                  label={cuban?.brand}
                                  isHidden={cuban?.field_status !== FieldStatus.ACTIVE}
                                  onToggle={() => {
                                    // Toggle cuban brand & update result as needed
                                    onUpdate(SiService.TOBACCO_SIFT_SERVICE, (data) => {
                                      data.detail[index].cuban_cigar_brands[cIndex].field_status =
                                        cuban?.field_status !== FieldStatus.ACTIVE
                                          ? FieldStatus.ACTIVE
                                          : FieldStatus.INACTIVE;

                                      const isBrandActive = data.detail.some((d) =>
                                        d?.cuban_cigar_brands?.some(
                                          (c) =>
                                            c?.brand === cuban?.brand &&
                                            c?.field_status === FieldStatus.ACTIVE
                                        )
                                      );
                                      data.result.cuban_cigar_brands.find(
                                        (c) => c?.brand === cuban?.brand
                                      ).field_status = isBrandActive
                                          ? FieldStatus.ACTIVE
                                          : FieldStatus.INACTIVE;
                                    });
                                  }}
                                  showToggle={isEditable}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <UnlabeledBulletColumns
                            data={getActiveItemsOnly(item?.cuban_cigar_brands, 'brand')}
                            columnCount={5}
                            columnWidth={'100px'}
                          />
                        )}
                      </div>
                    )}

                    {isEditable && (
                      <div className='d-flex flex-column gap-2'>
                        <div className='fw-bold'>{t('TOBACCO_KEYWORDS_FOUND')}</div>
                        <div className='d-flex flex-wrap gap-2'>
                          {item?.tobacco_keywords?.length > 0
                            ? item?.tobacco_keywords?.map((keyword, kIndex) => (
                              <div
                                className='badge badge-sm badge-secondary text-wrap text-start'
                                key={kIndex}
                              >
                                {keyword}
                              </div>
                            ))
                            : emDash}
                        </div>
                      </div>
                    )}
                  </div>
                );
              const isEditingUrl = isSiAiNewData && editingUrlKey === index;

              if (isEditable) {
                return (
                  <SingleAccordion
                    key={index}
                    header={
                      isEditingUrl ? (
                        <div
                          className='w-100 px-3 py-4'
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <input
                            className='form-control form-control-sm'
                            value={editingUrlValue}
                            onChange={(e) => setEditingUrlValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder='Enter URL'
                          />
                        </div>
                      ) : (
                        editableHeader
                      )
                    }
                    headerActions={
                      isSiAiNewData
                        ? isEditingUrl
                          ? (
                            <div className='d-flex align-items-center gap-2'>
                              <button
                                type='button'
                                className='btn btn-sm btn-light-success'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const trimmedUrl = editingUrlValue.trim();
                                  if (!trimmedUrl) {
                                    return;
                                  }

                                  onUpdate(SiService.TOBACCO_SIFT_SERVICE, (data) => {
                                    data.detail[index].url = trimmedUrl;
                                  });

                                  setEditingUrlKey(null);
                                  setEditingUrlValue('');
                                }}
                                disabled={!editingUrlValue.trim()}
                              >
                                Save
                              </button>
                              <button
                                type='button'
                                className='btn btn-sm btn-light'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingUrlKey(null);
                                  setEditingUrlValue('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingUrlKey(index);
                                setEditingUrlValue(item?.url ?? '');
                              }}
                            >
                              <i className='bi bi-pencil-square'></i>
                            </button>
                          )
                        : undefined
                    }
                    open
                    className={{
                      container: 'card border rounded',
                      body: 'p-0',
                    }}
                  >
                    {bodyContent}
                  </SingleAccordion>
                );
              }

              return (
                <div className='card border rounded' key={index}>
                  {/* URL Header */}
                  <div className='d-flex justify-content-between align-items-center gap-4 w-100 p-3'>
                    <div className='flex-grow-1 min-w-0 pe-2'>
                      <UrlLabelThumbnailCard
                        url={item.url}
                        thumbnail={images?.[0]}
                        onClickThumbnail={() => {
                          onExpandImages(images);
                        }}
                        showThumbnaiPlaceholder
                      />
                    </div>
                    <div className='text-end flex-shrink-0 d-flex align-items-center gap-2'>
                      <KeywordTypeBadge type={SiService.TOBACCO_SIFT_SERVICE} className='fs-8' />
                      {data?.result?.cuban && hasActiveCubanBrands && (
                        <div className='badge fw-bold fs-8 lh-sm' style={{ backgroundColor: '#875f41' }}>
                          {t('CUBAN')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Body */}
                  {bodyContent}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
